import { app, authentication } from '@microsoft/teams-js';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import axiosClient from '@/shared/api/axiosClient';
import { fetchMyInfo } from '@/shared/api/user';
import { useUser } from '@/app/providers/UserContext';
import { getMsalTokenProvider } from '@/app/providers/MsalTokenProvider';
import { getMsalConfigError, isMsalConfigured } from '@/shared/config/msalConfig';
import { getGlobalTokenProvider, setGlobalTokenProvider } from '@/shared/lib/refreshToken';
import { tokenStorage } from '@/shared/lib/tokenStorage';
import {
  subscribeAuthFailure,
  resetAuthFailureState,
  type AuthFailureReason,
} from '@/shared/lib/authEvents';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { NetworkError, isApiError } from '@/shared/api/errors';
import { isInTeams } from '@/shared/lib/isInTeams';

const USER_LOAD_FAILED_MESSAGE = '사용자 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.';
const NETWORK_ERROR_MESSAGE = '네트워크 연결을 확인해주세요.';

export interface BootstrapError {
  type: 'network' | 'auth' | 'unknown';
  message: string;
}

interface SsoTokenProviderProps {
  children: (error: BootstrapError | null) => ReactNode;
}

// 토큰 제공자 인터페이스 - 전략 패턴 적용
export interface TokenProvider {
  ready(): Promise<void>;
  getToken(): Promise<string>;
}

// Mock 토큰 제공자
class MockTokenProvider implements TokenProvider {
  async getToken(): Promise<string> {
    try {
      // 환경변수에서 Mock 유저 타입 가져오기 (기본값: developer)
      const userType = import.meta.env.VITE_MOCK_USER_TYPE || 'developer';
      const endpoint = `/api/auth/mock-token/${userType}`;

      const response = await axiosClient.get<{ token: string }>(endpoint);
      if (!response.data || !response.data.token) {
        throw new Error('토큰이 응답에 포함되어 있지 않습니다.');
      }
      return response.data.token;
    } catch (error) {
      console.error('Mock 토큰 가져오기 실패:', error instanceof Error ? error.message : error);
      throw error;
    }
  }

  async ready(): Promise<void> {
    // Mock provider는 즉시 준비 완료
    return Promise.resolve();
  }
}

// Teams 토큰 제공자
class TeamsTokenProvider implements TokenProvider {
  private initPromise: Promise<void>;

  constructor() {
    this.initPromise = app.initialize();
  }

  async ready(): Promise<void> {
    return this.initPromise;
  }

  async getToken(): Promise<string> {
    // Requesting Teams SSO token
    await this.ready();
    return authentication.getAuthToken();
  }
}

const SsoTokenProvider = ({ children }: SsoTokenProviderProps) => {
  const [error, setError] = useState<BootstrapError | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingLabel, setLoadingLabel] = useState('인증 정보를 확인하는 중...');
  // UserContext: 전역 유저 정보 설정
  const { user: currentUser, setUser } = useUser();
  const isReauthInProgress = useRef(false);

  // 환경에 맞는 토큰 제공자 생성
  // 우선순위: Teams > Mock > MSAL
  const getTokenProvider = useCallback((): TokenProvider | null => {
    if (isInTeams) {
      return new TeamsTokenProvider();
    } else if (
      import.meta.env.MODE === 'development' &&
      import.meta.env.VITE_MOCK_TOKEN === 'true'
    ) {
      return new MockTokenProvider();
    } else if (isMsalConfigured()) {
      return getMsalTokenProvider();
    }
    return null;
  }, [isInTeams]);

  // 초기 토큰/유저 정보 획득
  useEffect(() => {
    let isMounted = true;

    const provider = getTokenProvider();
    if (provider) {
      setGlobalTokenProvider(provider);
    } else {
      const configError = getMsalConfigError();
      setError({
        type: 'auth',
        message:
          configError ?? '인증되지 않았습니다. Microsoft Teams 또는 개발 모드에서 실행해 주세요.',
      });
      setIsLoading(false);
      return;
    }

    // 1) 유저 정보 로딩 (Teams/Mock)
    const loadUser = async () => {
      try {
        if (currentUser) {
          return; // 이미 설정된 경우 재설정 불필요
        }

        const shouldLoadUser =
          isInTeams ||
          (import.meta.env.MODE === 'development' && import.meta.env.VITE_MOCK_TOKEN === 'true') ||
          isMsalConfigured();

        if (shouldLoadUser) {
          await provider.ready();

          const { user, permissions } = await fetchMyInfo();
          if (isMounted) {
            setUser(user, permissions);
          }
        }
      } catch (e) {
        console.error('SsoTokenProvider - 유저 정보 로드 실패:', e);
        if (e instanceof NetworkError || (isApiError(e) && e.code === 'NETWORK_ERROR')) {
          throw e;
        }
        // 권한 정보 없이 라우트가 렌더되면 PermissionGuard가 통과되므로 전체 화면 에러로 막는다.
        // 401은 인터셉터가 재인증 핸들러를 먼저 동기 실행한 뒤 reject하므로(재인증 중이거나
        // 비활성 계정 에러가 이미 세팅됨) 그 상태를 덮어쓰지 않는다.
        if (isMounted && !isReauthInProgress.current) {
          setError((prev) => prev ?? { type: 'unknown', message: USER_LOAD_FAILED_MESSAGE });
        }
      }
    };

    // 2) 토큰 초기화와 사용자 정보 로딩을 순서대로 처리
    const initializeApp = async () => {
      // 이미 토큰이 있으면 사용자 정보만 로딩
      const existingToken = tokenStorage.getToken();

      if (existingToken) {
        try {
          await loadUser();
        } catch (e) {
          if (isMounted && e instanceof NetworkError) {
            setError({ type: 'network', message: NETWORK_ERROR_MESSAGE });
            setIsLoading(false);
            return;
          }
        }
        // 재인증이 진행 중이면 loading 상태 유지 (재인증 핸들러가 관리)
        if (isMounted && !isReauthInProgress.current) {
          setIsLoading(false);
        }
        return;
      }

      // 토큰이 없으면 먼저 토큰 초기화
      try {
        // MSAL 환경에서는 redirect 직전임을 사용자에게 알림
        if (isMsalConfigured()) {
          setLoadingLabel('로그인이 필요합니다. Microsoft 로그인 페이지로 이동합니다...');
        }

        const provider = getGlobalTokenProvider();

        if (!provider) {
          if (isMounted) {
            setError({
              type: 'auth',
              message: '이 애플리케이션은 Microsoft Teams 또는 개발 모드에서만 실행할 수 있습니다.',
            });
            setIsLoading(false);
          }
          return;
        }

        const token = await provider.getToken();

        if (!token) {
          if (isMounted) {
            setError({ type: 'auth', message: '토큰을 가져올 수 없습니다.' });
            setIsLoading(false);
          }
          return;
        }

        tokenStorage.setToken(token);

        // 토큰 설정 후 사용자 정보 로딩
        await loadUser();

        if (isMounted && !isReauthInProgress.current) {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('SsoTokenProvider - 앱 초기화 실패:', err);
        if (isMounted && !isReauthInProgress.current) {
          if (err instanceof NetworkError) {
            setError({ type: 'network', message: NETWORK_ERROR_MESSAGE });
          } else {
            setError({
              type: 'unknown',
              message: `인증 실패: ${err instanceof Error ? err.message : String(err)}`,
            });
          }
          setIsLoading(false);
        }
      }
    };

    void initializeApp();

    return () => {
      isMounted = false;
    };
  }, [getTokenProvider, currentUser, setUser, isInTeams]);

  // 인증 실패 이벤트 구독 — 재인증 UX 시작
  useEffect(() => {
    let isMounted = true;

    const unsubscribe = subscribeAuthFailure((reason: AuthFailureReason) => {
      if (!isMounted || isReauthInProgress.current) return;

      // 비활성 계정(퇴사자 등) → 재인증 시도 없이 에러 화면 표시
      if (reason === 'ACCOUNT_DISABLED') {
        tokenStorage.setToken(null);
        setUser(null, null);
        setError({
          type: 'auth',
          message: '현재 사용할 수 없는 계정입니다. 관리자에게 문의해 주세요.',
        });
        setIsLoading(false);
        resetAuthFailureState();
        return;
      }

      isReauthInProgress.current = true;

      // 1) 상태 초기화
      tokenStorage.setToken(null);
      setUser(null, null);
      setError(null);
      setIsLoading(true);
      setLoadingLabel(
        reason === 'TOKEN_EXPIRED'
          ? '세션이 만료되었습니다. 다시 로그인합니다...'
          : '인증 정보를 확인할 수 없습니다. 다시 로그인합니다...',
      );

      // 2) 재인증 시도
      const provider = getGlobalTokenProvider();
      if (!provider) {
        setIsLoading(false);
        isReauthInProgress.current = false;
        resetAuthFailureState();
        return;
      }

      provider
        .getToken()
        .then(async (token) => {
          if (!isMounted) return;
          if (!token) {
            setIsLoading(false);
            return;
          }
          tokenStorage.setToken(token);
          try {
            const { user, permissions } = await fetchMyInfo();
            if (isMounted) {
              setUser(user, permissions);
            }
          } catch (e) {
            console.error('SsoTokenProvider - 재인증 후 유저 정보 로드 실패:', e);
            // 인터셉터가 토큰을 지운 경우(재인증 직후 401 등)에는 SsoTokenGate가 재로그인 화면을 렌더한다.
            // 토큰이 남아 있는데 실패했다면(재시도 후 401 포함) 권한 없이 라우트가 열리지 않도록
            // 전체 화면 에러로 막는다.
            const isTokenCleared = !tokenStorage.getToken();
            if (isMounted && !isTokenCleared) {
              setError(
                e instanceof NetworkError
                  ? { type: 'network', message: NETWORK_ERROR_MESSAGE }
                  : { type: 'unknown', message: USER_LOAD_FAILED_MESSAGE },
              );
            }
          }
          if (isMounted) {
            setIsLoading(false);
          }
        })
        .catch((err: unknown) => {
          console.warn(
            'SsoTokenProvider - 재인증 실패:',
            err instanceof Error ? err.message : String(err),
          );
          if (isMounted) {
            // error=null, token=null → SsoTokenGate가 AuthRequiredError 렌더링
            setIsLoading(false);
          }
        })
        .finally(() => {
          isReauthInProgress.current = false;
          resetAuthFailureState();
        });
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [setUser]);

  if (isLoading) {
    return <LoadingScreen label={loadingLabel} />;
  }

  return <>{children(error)}</>;
};

export default SsoTokenProvider;
